// src/components/layout/Sidebar.js
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, Users2, UserCog, Server, Settings, ChevronRight,
  LogOut, Shield, Palette, Package, X, Menu, Activity, BarChart3,
  Calendar, Container, Receipt, Ticket, Database, Globe2, Mail,
  Terminal, Clock, Bell
} from 'lucide-react';

const ICON_MAP = {
  'layout-dashboard': LayoutDashboard,
  'users-2':          Users2,
  'user-cog':         UserCog,
  'server':           Server,
  'bar-chart-3':      BarChart3,
  'calendar':         Calendar,
  'activity':         Activity,
  'box':              Container,
  'receipt':          Receipt,
  'ticket':           Ticket,
  'database-backup':  Database,
  'globe-2':          Globe2,
  'mail':             Mail,
  'terminal':         Terminal,
  'clock':            Clock,
  'bell-ring':        Bell,
};

const ADMIN_LINKS = [
  { path: '/settings',       label: 'Setări',   icon: Settings },
  { path: '/themes',         label: 'Teme',     icon: Palette },
  { path: '/module-manager', label: 'Module',   icon: Package },
];

export default function Sidebar() {
  const { user, modules, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '20px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 36, height: 36, background: 'var(--accent)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Shield size={20} color="white" />
        </div>
        {!collapsed && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>CRM Modular</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>v1.3.0</div>
          </div>
        )}
      </div>

      {/* User info */}
      {!collapsed && (
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, background: 'var(--bg-hover)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: 'var(--accent)', flexShrink: 0 }}>
            {user?.full_name?.[0] || user?.username?.[0] || '?'}
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.full_name || user?.username}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'capitalize' }} className={`role-${user?.role}`}>{user?.role}</div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: 1, padding: '0 8px 8px', textTransform: 'uppercase' }}>
          {!collapsed && 'Navigare'}
        </div>
        {modules.map(mod => {
          const Icon = ICON_MAP[mod.icon] || Package;
          return (
            <NavLink key={mod.id} to={mod.route}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px',
                borderRadius: 8, textDecoration: 'none', marginBottom: 2,
                color: isActive ? 'white' : 'var(--text-secondary)',
                background: isActive ? 'var(--accent)' : 'transparent',
                fontWeight: isActive ? 600 : 400, fontSize: 14, transition: 'all 0.15s',
              })}
              onMouseEnter={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = 'transparent'; }}
            >
              <Icon size={18} style={{ flexShrink: 0 }} />
              {!collapsed && <span style={{ flex: 1 }}>{mod.display_name}</span>}
              {!collapsed && <ChevronRight size={14} style={{ opacity: 0.4 }} />}
            </NavLink>
          );
        })}

        {/* Admin links */}
        {hasRole('superadmin', 'admin') && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: 1, padding: '16px 8px 8px', textTransform: 'uppercase' }}>
              {!collapsed && 'Administrare'}
            </div>
            {ADMIN_LINKS.map(link => {
              const Icon = link.icon;
              return (
                <NavLink key={link.path} to={link.path}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px',
                    borderRadius: 8, textDecoration: 'none', marginBottom: 2,
                    color: isActive ? 'white' : 'var(--text-secondary)',
                    background: isActive ? 'var(--accent)' : 'transparent',
                    fontWeight: isActive ? 600 : 400, fontSize: 14, transition: 'all 0.15s',
                  })}
                >
                  <Icon size={18} style={{ flexShrink: 0 }} />
                  {!collapsed && <span style={{ flex: 1 }}>{link.label}</span>}
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      {/* Footer */}
      <div style={{ padding: '12px 8px', borderTop: '1px solid var(--border)' }}>
        <button onClick={handleLogout} className="btn btn-secondary" style={{ width: '100%', justifyContent: collapsed ? 'center' : 'flex-start' }}>
          <LogOut size={16} />
          {!collapsed && 'Deconectare'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside style={{
        position: 'fixed', left: 0, top: 0, bottom: 0,
        width: collapsed ? 64 : 'var(--sidebar-width)',
        background: 'var(--bg-secondary)', borderRight: '1px solid var(--border)',
        zIndex: 100, transition: 'width 0.2s ease', overflow: 'hidden',
      }}>
        <button onClick={() => setCollapsed(!collapsed)}
          style={{ position: 'absolute', top: 20, right: -12, width: 24, height: 24, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
          <ChevronRight size={12} color="var(--text-secondary)" style={{ transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
        </button>
        <SidebarContent />
      </aside>

      {/* Mobile */}
      <button onClick={() => setMobileOpen(true)} className="btn-icon"
        style={{ position: 'fixed', top: 16, left: 16, zIndex: 200, display: 'none' }}>
        <Menu size={20} />
      </button>
      {mobileOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex' }}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.5)' }} onClick={() => setMobileOpen(false)} />
          <div style={{ width: 260, background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border)', position: 'relative' }}>
            <button onClick={() => setMobileOpen(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <X size={20} />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      <style>{`
        .main-content { margin-left: ${collapsed ? '64px' : 'var(--sidebar-width)'}; transition: margin 0.2s ease; }
        @media (max-width: 768px) { aside { display: none; } .main-content { margin-left: 0 !important; } }
      `}</style>
    </>
  );
}
